import { TemplateResult, html, nothing, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import innerStyles from './outline-kyruus-location-facet.inner.pcss?inline';
import globalStyles from './outline-kyruus-location-facet.global.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';

/**
 * The Kyruus Location Facet Component
 * @element outline-kyruus-location-facet
 *
 * @slot facet-label - Slot to add a label for the facet
 * @slot facet-reset - Slot to add a reset button for the facet
 * @slot facet-form - Slot to add the form for the facet
 */

@customElement('outline-kyruus-location-facet')
export class OutlineKyruusLocationFacet extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  // Loading for the location facet.
  @state() loading = false;

  protected firstUpdated(_changedProperties: Map<string, unknown>): void {
    super.firstUpdated(_changedProperties);
    this.querySelector("[slot='facet-form'] #distance")?.addEventListener(
      'change',
      e => this.distanceFilterSubmit(e)
    );
  }

  render(): TemplateResult {
    return html`
      <div class="facet-wrapper">
        <div class="facet-top">
          <slot name="facet-label"></slot>
          <slot name="facet-reset"></slot>
        </div>
        <slot name="facet-form"></slot>
        <button
          class="facet-use-my-location"
          @click="${this.useMyLocation}"
          ?disabled="${this.loading}"
        >
          Use My Location
          ${this.loading ? html`<div class="spinner"></div>` : nothing}
        </button>
      </div>
    `;
  }

  private useMyLocation(): void {
    // Set our loading state.
    this.loading = true;

    navigator.geolocation.getCurrentPosition(
      position => {
        // Get the elements we'll need to use.
        const locationInput = this.querySelector<HTMLInputElement>('#location');
        const submitButton = this.querySelector<HTMLButtonElement>(
          'button[type="submit"]'
        );

        this.loading = false;

        // Set the input value and execute the search.
        if (locationInput && submitButton) {
          locationInput.value = `${position.coords.latitude}, ${position.coords.longitude}`;
          submitButton.click();
        }
      },
      error => {
        this.loading = false;
        console.error('Error getting location:', error);
      }
    );
  }

  /**
   * Handles the submission of the distance filter form.
   * Prevents default form submission behavior, checks for location input and submit button,
   * and triggers a click on the submit button if both are present.
   *
   * @param e - The event object.
   */
  private distanceFilterSubmit(e: Event): void {
    e.preventDefault();

    const locationInput = this.querySelector<HTMLInputElement>('#location');

    // Check if the location input element is found. If not, bail.
    if (!locationInput || !locationInput.value) {
      return;
    }

    const submitButton = this.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    );

    // Check if the submit button element is found. If not, bail.
    if (!submitButton) {
      return;
    }

    submitButton.click();
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'outline-kyruus-location-facet': OutlineKyruusLocationFacet;
  }
}
